/**
 * Frontend RSA Hybrid Decryption utility.
 *
 * Decrypts data that was RSA-hybrid-encrypted by the server using its private key.
 * The frontend uses the RSA public key to recover the AES DEK (publicDecrypt),
 * then AES-256-GCM decrypts the actual payload.
 *
 * Since Web Crypto API does not support RSA publicDecrypt (recovering data encrypted
 * with a private key), we use node-forge for the raw RSA math and PKCS#1 v1.5 unpadding.
 * AES-GCM decryption uses the native Web Crypto API for performance.
 *
 * The RSA public key is fetched from GET /api/credentialPublicKeys at runtime
 * (once per session, cached in-memory). Key rotation takes effect on the next
 * session refresh without requiring a frontend rebuild.
 */

import forge from 'node-forge';

// Module-level cache — resolved once per browser session
let publicKeysPromise = null;

const getPublicKeys = () => {
  if (!publicKeysPromise) {
    //Detect the URL
    const rootUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
    let url = '/api/credentialPublicKeys';

    if (rootUrl) {
      if (rootUrl.includes('localhost')) {
        url = 'http://localhost:4000/api/credentialPublicKeys';
      } else {
        url = `${rootUrl}/api/credentialPublicKeys`;
      }
    }

    publicKeysPromise = fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch credential public keys');
        return res.json();
      })
      .catch(err => {
        // Reset so the next call retries
        publicKeysPromise = null;
        throw err;
      });
  }
  return publicKeysPromise;
};

/**
 * RSA publicDecrypt — recovers data that was encrypted with the private key.
 * Equivalent to Node.js crypto.publicDecrypt with RSA_PKCS1_PADDING.
 *
 * Performs raw RSA: m = c^e mod n, then strips PKCS#1 v1.5 type-1 padding.
 *
 * @param {string} publicKeyPem    – PEM-encoded RSA public key
 * @param {string} encryptedBase64 – base64-encoded ciphertext
 * @returns {Uint8Array}           – the recovered plaintext bytes
 */
function rsaPublicDecryptRaw(publicKeyPem, encryptedBase64) {
  const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
  const encryptedBytes = forge.util.decode64(encryptedBase64);
  const keySize = Math.ceil(publicKey.n.bitLength() / 8);

  // Raw RSA: m = c^e mod n
  const c = new forge.jsbn.BigInteger(forge.util.bytesToHex(encryptedBytes), 16);
  const m = c.modPow(publicKey.e, publicKey.n);

  // Convert BigInteger to byte string, zero-padded to key size
  let mHex = m.toString(16);
  while (mHex.length < keySize * 2) {
    mHex = '0' + mHex;
  }
  const mBytes = forge.util.hexToBytes(mHex);

  // Verify and strip PKCS#1 v1.5 type-1 padding: 0x00 0x01 [0xFF...] 0x00 [data]
  if (mBytes.charCodeAt(0) !== 0x00 || mBytes.charCodeAt(1) !== 0x01) {
    throw new Error('RSA publicDecrypt: invalid PKCS#1 v1.5 padding');
  }
  let i = 2;
  while (i < mBytes.length && mBytes.charCodeAt(i) === 0xff) {
    i++;
  }
  if (i >= mBytes.length || mBytes.charCodeAt(i) !== 0x00) {
    throw new Error('RSA publicDecrypt: missing padding separator');
  }

  // Extract the data after padding
  const dataStr = mBytes.substring(i + 1);
  const result = new Uint8Array(dataStr.length);
  for (let j = 0; j < dataStr.length; j++) {
    result[j] = dataStr.charCodeAt(j);
  }
  return result;
}

/**
 * RSA Hybrid Decrypt — decrypts an RSA-hybrid-encrypted envelope using the public key.
 *
 * The envelope was created by the server using rsaHybridEncrypt (privateEncrypt for DEK).
 * 1. RSA publicDecrypt recovers the 256-bit AES DEK.
 * 2. AES-256-GCM (Web Crypto API) decrypts the payload.
 *
 * @param {Object} envelope        – { encryptedDek, iv, ct, tag } (all base64-encoded)
 * @param {string} [publicKeyPem]  – optional PEM key override
 * @returns {Promise<Object>}      – the decrypted payload (parsed from JSON)
 */
export async function rsaHybridDecrypt(envelope, publicKeyPem) {
  if (!publicKeyPem) {
    const keys = await getPublicKeys();
    publicKeyPem = keys.rsaPublicKey.replace(/\\n/g, '\n');
  }

  if (!publicKeyPem) {
    throw new Error('RSA public key not available. Check /api/credentialPublicKeys endpoint.');
  }

  // Step 1: RSA publicDecrypt the DEK
  const dekBytes = rsaPublicDecryptRaw(publicKeyPem, envelope.encryptedDek);

  // Step 2: Decode base64 components
  const iv = Uint8Array.from(atob(envelope.iv), c => c.charCodeAt(0));
  const ct = Uint8Array.from(atob(envelope.ct), c => c.charCodeAt(0));
  const tag = Uint8Array.from(atob(envelope.tag), c => c.charCodeAt(0));

  // Step 3: Concatenate ciphertext + auth tag (Web Crypto AES-GCM expects this)
  const combined = new Uint8Array(ct.length + tag.length);
  combined.set(ct);
  combined.set(tag, ct.length);

  // Step 4: Import DEK as CryptoKey
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    dekBytes,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  // Step 5: AES-256-GCM decrypt
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    cryptoKey,
    combined
  );

  return JSON.parse(new TextDecoder().decode(decryptedBuffer));
}
